import { useLocation, useNavigate } from "react-router-dom";
import { Clock, LogOut, RefreshCw } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import CinematicLayout from "../../components/CinematicLayout";
import { useState } from "react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PendingApproval = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = location.state?.user;
  const [checking, setChecking] = useState(false);

  const handleLogout = async () => {
    try {
      await fetch(`${API}/auth/staff/logout`, {
        method: "POST",
        credentials: "include"
      });
    } catch {
      // Silent fail - logout error is non-critical
    }
    navigate("/staff/login", { replace: true });
  };

  const checkStatus = async () => {
    setChecking(true);
    try {
      const response = await fetch(`${API}/auth/staff/me`, {
        credentials: "include"
      });
      if (response.ok) {
        const userData = await response.json();
        if (userData.status === "active") {
          toast.success("Your account has been approved!");
          navigate("/dashboard", { replace: true });
        } else if (userData.status === "denied") {
          toast.error("Your account has been denied access");
          navigate("/staff/login", { state: { error: "Account denied" }, replace: true });
        } else {
          toast.info("Still pending approval");
        }
      }
    } catch (error) {
      toast.error("Could not check status");
    } finally {
      setChecking(false);
    }
  };

  return (
    <CinematicLayout showLogo>
      {/* Content */}
      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-6">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="mb-8 text-center">
            <img
              src="/logo-reflection-trimmed.png"
              alt="DopeInHD Incorporated"
              className="h-12 w-auto mx-auto"
            />
          </div>

          {/* Pending Card */}
          <Card className="bg-[#121214]/90 backdrop-blur-sm border-[#27272A]">
            <CardContent className="pt-8 pb-8">
              <div className="w-20 h-20 rounded-full bg-[#F59E0B]/20 flex items-center justify-center mx-auto mb-6 animate-pulse">
                <Clock size={40} className="text-[#F59E0B]" />
              </div>
              
              <h1 className="text-2xl font-bold text-[#FAFAFA] mb-2 text-center">
                Awaiting Approval
              </h1>
              
              <p className="text-[#A1A1AA] mb-6 text-center">
                Your account is pending admin approval. You'll be able to access the Command Center once approved.
              </p>

              {user && (
                <div className="bg-[#1A1A1D] rounded-lg p-4 mb-6">
                  <div className="flex items-center gap-3 justify-center">
                    {user.picture ? (
                      <img src={user.picture} alt={user.name} className="w-12 h-12 rounded-full border-2 border-[#D4AF37]" />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-[#D4AF37] flex items-center justify-center text-[#0A0A0B] font-bold">
                        {user.name?.charAt(0)}
                      </div>
                    )}
                    <div className="text-left">
                      <p className="font-medium text-[#FAFAFA]">{user.name}</p>
                      <p className="text-sm text-[#71717A]">{user.email}</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-3">
                <p className="text-xs text-[#71717A] text-center">
                  An admin will review your request shortly. Check back later or contact your team lead.
                </p>
                
                <Button
                  onClick={checkStatus}
                  disabled={checking}
                  className="w-full bg-gradient-to-r from-[#D4AF37] to-[#B8952F] text-[#0A0A0B] font-semibold hover:from-[#E5C558] hover:to-[#D4AF37]"
                >
                  {checking ? (
                    <RefreshCw size={16} className="mr-2 animate-spin" />
                  ) : (
                    <RefreshCw size={16} className="mr-2" />
                  )}
                  Check Status
                </Button>
                
                <Button
                  onClick={handleLogout}
                  variant="outline"
                  className="w-full border-[#27272A] text-[#A1A1AA] hover:bg-[#27272A]"
                >
                  <LogOut size={16} className="mr-2" />
                  Sign Out
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Footer */}
          <p className="text-[#71717A] text-xs mt-8 text-center">
            © 2025 DopeInHD Incorporated
          </p>
        </div>
      </div>
    </CinematicLayout>
  );
};

export default PendingApproval;
